/**
 * 1xUX tuning surface — all first-time user experience knobs in one place.
 *
 * This is the primary file to edit when testing 1xUX variants:
 *   - Chat copy (greeting, placeholder, loading messages)
 *   - Chat pacing (message budget, progress bar)
 *   - Chat reliability (stream timeouts)
 *   - Canvas reveal animation timings
 *
 * Related tuning surfaces:
 *   - System prompt / persona: src/lib/onboarding/prompt.ts
 *   - Canvas canned messages: src/lib/levai/events/onboarding.ts
 *   - Create All scope: `includeInCreateAll` field on each step in src/constants/steps.ts
 *   - Live config (dev only): GET /api/onboarding/chat → { systemPrompt, modelId, reasoningEffort, maxUserMessages }
 *
 * See .claude/skills/1xux/SKILL.md for the full 1xUX testing guide.
 */

// ─── Copy ────────────────────────────────────────────────────────────────────

/** Lev's greeting headline — always visible in the onboarding chat */
export const LEV_GREETING_HEADLINE = "Let's figure out your startup together.";

const LEV_GREETING_DETAILS_LINES = [
  "Pick whatever's fastest for you:",
  "",
  "- **Just start typing** — tell me about your idea in a few sentences",
  "- **Paste a link** — drop your website or landing page URL",
  "- **Upload a document** — pitch deck, one-pager, or business plan",
  "- **Ask me for an idea** — we can brainstorm something together",
  "",
  "The more context you give me, the faster we'll move!",
] as const;

/** Lev's greeting details — streamed as a second message after a short onboarding delay */
export const LEV_GREETING_DETAILS = LEV_GREETING_DETAILS_LINES.join("\n");

/**
 * Incremental intro frames for the fake onboarding stream.
 * Each frame keeps markdown balanced so bold/list rendering stays stable while animating.
 */
export const LEV_GREETING_DETAILS_STREAM_FRAMES = [
  LEV_GREETING_DETAILS_LINES.slice(0, 2).join("\n"),
  LEV_GREETING_DETAILS_LINES.slice(0, 3).join("\n"),
  LEV_GREETING_DETAILS_LINES.slice(0, 4).join("\n"),
  LEV_GREETING_DETAILS_LINES.slice(0, 6).join("\n"),
  LEV_GREETING_DETAILS,
] as const;

/** Full greeting (headline + details) — used by saved-chat restore and tests */
export const LEV_GREETING = `${LEV_GREETING_HEADLINE}\n\n${LEV_GREETING_DETAILS}`;

/** Placeholder text shown in the onboarding chat textarea */
export const INPUT_PLACEHOLDER = "Describe your idea, paste a link, or upload a doc...";


/** Rotating loading messages shown during onboarding transitions (post-build and canvas loading) */
export const ONBOARDING_LOADING_MESSAGES = [
  "Synthesizing your Big Idea…",
  "Setting up your Canvas…",
  "Ideating…",
  "Crafting your strategy…",
  "Mapping your market…",
  "Thinking like your customer…",
  "Sizing the opportunity…",
  "Building your playbook…",
  "Sharpening your edge…",
  "Finding your differentiator…",
  "Plotting your go-to-market…",
  "Analyzing the competition…",
  "Dialing in your positioning…",
  "Laying the foundation…",
] as const;

/** Messages for the canvas loading overlay (excludes the first chat-only message) */
export const CANVAS_LOADING_MESSAGES = ONBOARDING_LOADING_MESSAGES.slice(1);

// ─── Chat pacing ─────────────────────────────────────────────────────────────

/** Maximum number of user messages allowed in the onboarding chat (client + server cap). */
export const MAX_USER_MESSAGES = 20;

/** Show a "running low on messages" warning when user count reaches this threshold */
export const WARNING_THRESHOLD = 18;

/** Server-side soft limit: always equal to MAX_USER_MESSAGES. */
export const SOFT_MESSAGE_LIMIT = MAX_USER_MESSAGES;

/**
 * Default max time "Build" waits for an in-flight deck/site parse to settle
 * before creating the project anyway (ADR 056), used when the
 * BUILD_PARSE_WAIT_CAP_MS env var is unset or invalid. Server code MUST read the
 * cap via resolveBuildParseWaitCapMs, never this constant: this file ships in
 * client bundles, so it cannot see the env. After the cap, creation proceeds and
 * late parse facts land project-scoped (resolveOnboardingFactScope).
 */
export const BUILD_PARSE_WAIT_CAP_MS = 30_000;

/**
 * Cumulative progress bar increments per user question (sums to 95%; holds there until
 * status = "complete" triggers 100%).
 */
export const PROGRESS_INCREMENTS = [15, 20, 20, 20, 15, 5] as const;

// ─── Chat reliability ────────────────────────────────────────────────────────

/**
 * Client-side stream timeout in ms. Intentionally lower than the server's
 * maxDuration (60s) so the client can detect a stalled stream and recover
 * before the server hard-kills the connection.
 */
export const STREAM_TIMEOUT_MS = 45_000;

/** No-data heartbeat timeout during active streaming. Triggers recovery if stream goes silent. */
export const HEARTBEAT_TIMEOUT_MS = 30_000;

/** Interval between rotating loading message transitions */
export const ONBOARDING_MESSAGE_INTERVAL_MS = 3500;

// ─── Canvas reveal animation ─────────────────────────────────────────────────

/**
 * Timing constants for the first-canvas reveal sequence.
 *
 * Sequence:
 *   1. Big Idea card appears on canvas
 *   2. Wait bigIdeaPauseMs → lightbox zooms in from card
 *   3. Wait levaiSidebarDelayMs → LevAI panel + sidebar slide in
 *
 * Increase these values for a more "cinematic" feel; decrease for a snappier experience.
 */
export const REVEAL_TIMING = {
  /** Pause (ms) between Big Idea card appearing and the lightbox zoom-in animation */
  bigIdeaPauseMs: 800,
  /** Delay (ms) after lightbox opens before LevAI panel and sidebar slide in */
  levaiSidebarDelayMs: 1000,
} as const;

/**
 * Reveal pacing for the post-onboarding flow when a user clicks a task card
 * in the sidebar. Chat and block lightbox open simultaneously, but the
 * lightbox renders in a loading state for `contentRevealMs` so the user reads
 * the task copy in chat first before block content competes for attention.
 */
export const TASK_REVEAL_TIMING = {
  /** How long the block lightbox stays in loading state (grayed actions +
   *  spinner) after a task card click before its content fades in. */
  contentRevealMs: 2000,
} as const;

/** Poll interval while onboarding completion waits for materials parse (LEV-1360). */
export const MATERIALS_PARSE_POLL_INTERVAL_MS = 3000;
